import { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { NEXT_STEP_LABELS } from '@/types';
import type { NextStep } from '@/types';
import { Loader2 } from 'lucide-react';

const QUICK_STEPS: NextStep[] = ['call', 'follow_up', 'appointment', 'send_numbers'];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateName?: string;
  onConfirm: (nextStep: NextStep) => Promise<void> | void;
  busy?: boolean;
}

export function NextStepPickerSheet({ open, onOpenChange, templateName, onConfirm, busy }: Props) {
  const [showAll, setShowAll] = useState(false);
  const allSteps = Object.keys(NEXT_STEP_LABELS) as NextStep[];
  const visible = showAll ? allSteps : QUICK_STEPS;

  const pick = async (step: NextStep) => {
    await onConfirm(step);
    setShowAll(false);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader className="text-left">
          <SheetTitle>What's the next step?</SheetTitle>
          <SheetDescription>
            {templateName ? `Logging "${templateName}". Pick the next move.` : 'Pick the next move for this customer.'}
          </SheetDescription>
        </SheetHeader>
        <div className="grid grid-cols-2 gap-2 mt-4">
          {visible.map((step) => (
            <Button
              key={step}
              variant="outline"
              size="lg"
              disabled={busy}
              onClick={() => pick(step)}
              className="justify-start h-12"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {NEXT_STEP_LABELS[step]}
            </Button>
          ))}
        </div>
        {!showAll && (
          <Button variant="ghost" size="sm" className="w-full mt-3" onClick={() => setShowAll(true)} disabled={busy}>
            More options…
          </Button>
        )}
      </SheetContent>
    </Sheet>
  );
}